import { useState, useRef, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { InvestmentTransaction, InvestmentTransactionList } from '../types/investmentApi.types'
import TransactionRow from '../components/investment/transactions/TransactionRow'
import BackButton from '../../../components/ui-components/BackButton'
import EndOfList from '../components/investment/transactions/EndOfList'
import TransactionsSkeleton from '../../../components/skeletons/TransactionsSkeleton'
import { getBottomPadding, getTopPadding } from '../../../utils/sessionStorage'
import { fetchInvsetmentTransactions } from '../repository/userInvestmentApi'

export default function InvestmentTransactionsPageV2() {
  const [transactions, setTransactions] = useState<InvestmentTransaction[]>([])
  const [totalSize, setTotalSize] = useState(0)
  const [fundName, setFundName] = useState('')
  const { isin } = useParams<{ isin: string }>()
  const [searchParams] = useSearchParams()
  const temporaryFamilyAccessToken = searchParams?.get('temporaryFamilyAccessToken') ?? ''
  const observer = useRef<any>()
  const backButtonRef = useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()
  const calledTimeStamps = useRef<number[]>([])

  // Move initial focus to Back so TalkBack lands on it first (Transaction History_B01)
  useEffect(() => {
    const id = window.setTimeout(() => {
      backButtonRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(id)
  }, [])

  // @ts-ignore
  const { mutate, isPending } = useMutation<any, Error>({
    mutationKey: ['investmentTransaction'],
    mutationFn: (timestamp) => {
      // @ts-ignore
      return fetchInvsetmentTransactions(isin, 20, timestamp, temporaryFamilyAccessToken || undefined)
    },
    onSuccess: (data) => {
      const newTransactions = data?.data as InvestmentTransactionList
      setTotalSize(() => newTransactions?.itemsCount)
      setTransactions((transactionsPrev) => {
        return [...transactionsPrev, ...newTransactions?.userTxnResponseList]
      })
    }
  })

  useEffect(() => {
    // @ts-ignore
    mutate(Date.now())
  }, [])

  useEffect(() => {
    setFundName(() => searchParams?.get('fundName') ?? '')
  }, [searchParams])

  useEffect(() => {
    if (transactions.length > 0) {
      const txnDetailListElements = document.querySelectorAll('.txn-detail')
      const lastIndex = txnDetailListElements.length - 1
      const lastChild = txnDetailListElements[lastIndex]

      if (lastChild) {
        observer.current = new IntersectionObserver(async (entries) => {
          const observeOnList = entries[0]
          if (observeOnList.isIntersecting) {
            if (!isPending) {
              const offsetTimeStamp = transactions[transactions.length - 1].timestamp
              if (transactions?.length < totalSize && !calledTimeStamps?.current?.includes(offsetTimeStamp)) {
                calledTimeStamps?.current?.push(offsetTimeStamp)
                // @ts-ignore
                await mutate(offsetTimeStamp)
              }
            }
          }
        })

        observer.current.observe(lastChild)
      }
    }
  }, [transactions])

  return (
    <div className='flex flex-col h-screen overflow-y-scroll no-scrollbar'>
      <header
        style={{ paddingTop: `calc(${getTopPadding()}px + 10px)` }}
        className='sticky top-0 z-10 bg-white px-5 pb-5'
      >
        <BackButton
          ref={backButtonRef}
          size={24}
          onClick={() => {
            navigate(-1)
          }}
        />
        <h1 className='text-[#000000] font-[500] m-0 mt-5 text-base'>Transaction History</h1>
        {fundName && <p className='text-13 text-[#6A6E6E] pt-1 max-w-[80%] m-0'>{fundName}</p>}
      </header>

      {/*
        Explicit role=list / listitem: list-style:none otherwise drops list announcement
        on iOS (Transaction History_B06; same pattern as Power select column reorder).
        The page wrapper scrolls, not the list: a named scrollable list with no focusable children
        becomes an extra TalkBack stop before the first transaction (Transaction History_B03).
        EndOfList is aria-hidden so it does not inflate "N of M" counts.
      */}
      <ul
        role='list'
        style={{ paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
        className='flex flex-col gap-8 px-5 pt-8 list-none m-0 p-0'
      >
        {transactions?.map((transaction, idx) => {
          return (
            <li
              key={idx.toString(2)}
              role='listitem'
              className='w-full txn-detail animate-fade-150 list-none'
            >
              <TransactionRow transaction={transaction} />
            </li>
          )
        })}
        {isPending && (
          <li className='list-none' aria-hidden='true'>
            <TransactionsSkeleton rows={20} />
          </li>
        )}
        {transactions?.length >= totalSize && !isPending && (
          <li className='list-none' aria-hidden='true'>
            <EndOfList />
          </li>
        )}
      </ul>
    </div>
  )
}
